/**
 * Une série d'automatismes : ce qu'elle affiche, et ce qu'elle refuse d'afficher.
 *
 * Le §10 d'`exercise` demande trois choses d'un flash, et la troisième est la
 * plus facile à trahir : une série d'items sur une même compétence — de
 * `SERIE_MIN` à `SERIE_MAX`, soit **1 à 10** depuis le 15 septembre 2026 —, un
 * **chronomètre annoncé et visible**, et une présentation « comme un
 * entraînement aux automatismes, jamais comme un test de rapidité ».
 *
 * D'où trois règles qui gouvernent tout ce fichier :
 *
 * 1. **Le chronomètre monte.** Il ne descend pas vers une limite, il n'affiche
 *    pas de durée à battre, et la réponse du serveur ne porte d'ailleurs aucun
 *    total (`SerieFlash`). Un compte à rebours transformerait l'entraînement en
 *    épreuve, et c'est exactement la phrase que le §10 interdit.
 * 2. **Il est annoncé avant de tourner.** Un chronomètre découvert en cours de
 *    route se lit comme un piège ; annoncé, il devient la règle du jeu. C'est
 *    aussi ce qui distingue cet écran du reste de la coquille élève, où la
 *    mesure du temps existe (`chrono`) et **ne se montre jamais** — un
 *    compteur visible sur un exercice ordinaire transformerait chaque soirée en
 *    épreuve chronométrée.
 * 3. **Les phrases s'accordent au nombre d'items reçus.** Le plancher de la
 *    série a valu cinq jusqu'au 15 septembre 2026 ; à 1, une annonce écrite au
 *    pluriel promet « des questions courtes » au-dessus d'une seule, et le
 *    rang affiche « Question 1 sur 1 ». Une série d'un item est un exercice
 *    ordinaire avec une horloge dessus — les mots du commentaire qui défendait
 *    le plancher de cinq —, et c'est ici que ça se voit : le serveur ne peut
 *    pas rattraper une promesse fausse, l'écran est le dernier endroit où elle
 *    se tient. D'où des fonctions et non des constantes.
 *
 * Ce qui n'apparaît nulle part ici : un score, un nombre de bonnes réponses, un
 * temps moyen, une comparaison avec la dernière fois (invariant §14.1). Le
 * compteur d'avancement est une position dans une file, pas un résultat.
 */

/**
 * Le chronomètre, tel qu'il se lit — `m:ss`, et des heures s'il le faut.
 *
 * Les secondes plutôt que les dixièmes : un affichage au dixième donne à lire
 * un temps de réaction, ce qui est la lecture qu'on ne veut pas. La seconde
 * suffit à montrer que ça tourne, et ne se compare pas.
 */
export function chronoEnClair(ms: number): string {
  const secondes = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(secondes / 3600);
  const m = Math.floor((secondes % 3600) / 60);
  const s = secondes % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}

/**
 * Où l'on en est dans la série.
 *
 * **Sur combien, et pas seulement le rang.** Une série se commence en sachant
 * qu'elle finit : c'est la moitié de ce qui rend un chronomètre acceptable, et
 * un « question 4 » sans horizon fait exactement l'inverse.
 *
 * À un seul item, le rang n'apprend rien — « Question 1 sur 1 » est une file
 * d'attente d'une personne — et l'horizon est déjà donné par la phrase.
 */
export function avancement(index: number, total: number): string {
  if (total <= 1) {
    return "Une seule question";
  }
  return `Question ${Math.min(index + 1, total)} sur ${total}`;
}

/**
 * Ce que l'écran dit avant de lancer quoi que ce soit.
 *
 * Écrit ici plutôt que dans le JSX pour la même raison que `ui/penurie` : c'est
 * une phrase qui tient une règle, elle se relit et elle se teste.
 *
 * **Deux phrases et non une tournure passe-partout.** « Des questions courtes »
 * au-dessus d'une seule question est faux, et « 1 question » l'est
 * grammaticalement ; un enfant qui lit une promesse démentie par l'écran
 * qu'elle annonce apprend surtout à ne plus lire l'annonce. Ce qui ne change
 * pas d'une version à l'autre est ce qui tient la règle du §10 : le chronomètre
 * est annoncé, et il n'y a pas de temps à battre.
 */
export function annonce(total: number): string {
  const commun =
    "Le chronomètre tourne pendant que tu la fais — il n'y a pas de temps à " +
    "battre, c'est juste pour mesurer les progrès.";
  if (total <= 1) {
    return `C'est un automatisme : une question courte, pour que ça devienne réflexe. ${commun}`;
  }
  return (
    "C'est une série d'automatismes : des questions courtes, sur la même " +
    `chose, pour que ça devienne réflexe. ${commun}`
  );
}

/**
 * Combien de questions, juste sous l'annonce.
 *
 * Le nombre est une information utile — on ne s'engage pas dans un chronomètre
 * sans savoir jusqu'où il tourne —, et à un item il se dit en mots : « 1
 * questions » est la faute d'accord que produit toujours un compte interpolé
 * dans une phrase écrite au pluriel.
 */
export function combienDeQuestions(total: number): string {
  if (total <= 1) {
    return "Une seule question, sur un seul point.";
  }
  return `${total} questions, toutes sur la même chose.`;
}

/**
 * Ce que l'écran dit quand la série est finie.
 *
 * La durée s'affiche parce qu'elle était visible tout du long, et **sans rien à
 * quoi la comparer** : ni la série d'avant, ni un objectif, ni une moyenne.
 * C'est la comparaison qui referait un test de rapidité, pas le chiffre.
 */
export function bilan(total: number, ms: number): string {
  const duree = chronoEnClair(ms);
  if (total <= 1) {
    return `Tu as fait la question en ${duree}.`;
  }
  return `Tu as fait les ${total} questions en ${duree}.`;
}
